import type { FormxFieldDto, FormxFormDto, FormxSubmissionDto, FormxValueDto } from '@/features/formx/data/dtos/FormxDto.js';
import { getSupabaseClient } from '@/core/supabase/client.js';
import { fetchAllPaged } from '@/core/supabase/pagination.js';

const FORM_FIELDS = `id, enabled, org_id, project_id, name, description, submission_limit, sort_order, created_at, updated_at`;
const FIELD_FIELDS = `id, enabled, form_id, org_id, code, label, hint, field_type, is_required, default_value, target, target_unit, metadatas, sort_order, created_at, updated_at`;
const SUBMISSION_FIELDS = `id, form_id, org_id, session_id, location_id, created_by, notes, submitted_at, created_at`;

export class FormxRemoteDataSource {
  private get supabase() { return getSupabaseClient() as any; }

  async listForms(options: { orgId: string; projectId?: string; search?: string; limit?: number; offset?: number }): Promise<{ data: FormxFormDto[]; total: number }> {
    const limit = options.limit ?? 100;
    const offset = options.offset ?? 0;
    const [rpcRes, countRes] = await Promise.all([
      this.supabase.rpc('search_formx_forms', {
        p_org_id: options.orgId,
        p_project_id: options.projectId ?? null,
        search_query: options.search ?? '',
        row_limit: limit,
        row_offset: offset,
      }),
      options.projectId
        ? this.supabase.from('formx_forms')
            .select('id', { count: 'exact', head: true })
            .eq('org_id', options.orgId)
            .eq('project_id', options.projectId)
        : this.supabase.from('formx_forms')
            .select('id', { count: 'exact', head: true })
            .eq('org_id', options.orgId),
    ]);
    if (rpcRes.error) throw new Error(rpcRes.error.message);
    return { data: rpcRes.data ?? [], total: countRes.count ?? 0 };
  }

  async getForm(id: string): Promise<FormxFormDto | null> {
    const { data, error } = await this.supabase.from('formx_forms').select(FORM_FIELDS).eq('id', id).maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async createForm(payload: Omit<FormxFormDto, 'id' | 'created_at' | 'updated_at'>): Promise<FormxFormDto | null> {
    const { data, error } = await this.supabase.from('formx_forms').insert(payload).select(FORM_FIELDS).maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async updateForm(id: string, payload: Partial<FormxFormDto>): Promise<FormxFormDto | null> {
    const { data, error } = await this.supabase.from('formx_forms').update(payload).eq('id', id).select(FORM_FIELDS).maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async deleteForm(id: string): Promise<void> {
    const { error } = await this.supabase.from('formx_forms').delete().eq('id', id);
    if (error) throw new Error(error.message);
  }

  async reorderForms(updates: Array<{ id: string; sortOrder: number }>): Promise<void> {
    const { error } = await this.supabase.rpc('reorder_formx_forms', {
      p_updates: updates.map((u) => ({ id: u.id, sort_order: u.sortOrder })),
    });
    if (error) throw new Error(error.message);
  }

  async listFields(formId: string): Promise<FormxFieldDto[]> {
    const { data, error } = await this.supabase.from('formx_fields').select(FIELD_FIELDS).eq('form_id', formId).order('sort_order');
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  async createField(payload: Omit<FormxFieldDto, 'id' | 'created_at' | 'updated_at'>): Promise<FormxFieldDto | null> {
    const { data, error } = await this.supabase.from('formx_fields').insert(payload).select(FIELD_FIELDS).maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async updateField(id: string, payload: Partial<FormxFieldDto>): Promise<FormxFieldDto | null> {
    const { data, error } = await this.supabase.from('formx_fields').update(payload).eq('id', id).select(FIELD_FIELDS).maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async deleteField(id: string): Promise<void> {
    const { error } = await this.supabase.from('formx_fields').delete().eq('id', id);
    if (error) throw new Error(error.message);
  }

  async reorderFields(updates: Array<{ id: string; sortOrder: number }>): Promise<void> {
    const { error } = await this.supabase.rpc('reorder_formx_fields', {
      p_updates: updates.map((u) => ({ id: u.id, sort_order: u.sortOrder })),
    });
    if (error) throw new Error(error.message);
  }

  async listSubmissions(options: { formId: string; limit?: number; offset?: number }): Promise<FormxSubmissionDto[]> {
    const buildBase = () => this.supabase.from('formx_submissions').select(SUBMISSION_FIELDS)
      .eq('form_id', options.formId)
      .order('created_at', { ascending: false }).order('id', { ascending: false });
    if (typeof options.limit === 'number' && typeof options.offset === 'number') {
      const { data, error } = await buildBase().range(options.offset, options.offset + options.limit - 1);
      if (error) throw new Error(error.message);
      return data ?? [];
    }
    if (typeof options.limit === 'number') {
      const { data, error } = await buildBase().limit(options.limit);
      if (error) throw new Error(error.message);
      return data ?? [];
    }
    // No limit given = caller wants everything; page past the PostgREST cap.
    return fetchAllPaged<FormxSubmissionDto>((from, to) => buildBase().range(from, to));
  }

  async createSubmissionWithValues(input: {
    formId: string;
    sessionId?: string | null;
    locationId?: string | null;
    notes?: string | null;
    values: Array<{ fieldId: string; value: string }>;
  }): Promise<string> {
    const { data, error } = await this.supabase.rpc('create_formx_submission', {
      p_form_id: input.formId,
      p_session_id: input.sessionId ?? null,
      p_location_id: input.locationId ?? null,
      p_notes: input.notes ?? null,
      p_values: input.values.map((v) => ({ field_id: v.fieldId, value: v.value })),
    });
    if (error) throw new Error(error.message);
    return data as string;
  }

  async listValueSummaryByForm(formId: string): Promise<Array<{ submission_id: string; field_id: string }>> {
    const { data, error } = await this.supabase
      .from('formx_values')
      .select('submission_id, field_id, submission:formx_submissions!inner(form_id)')
      .eq('submission.form_id', formId)
      .limit(5000);
    if (error) throw new Error(error.message);
    return (data ?? []).map((r: any) => ({ submission_id: r.submission_id, field_id: r.field_id }));
  }

  async listValuesBySubmissionIds(submissionIds: string[]): Promise<FormxValueDto[]> {
    const CHUNK = 100;
    const PAGE = 1000;
    const out: FormxValueDto[] = [];
    for (let i = 0; i < submissionIds.length; i += CHUNK) {
      const chunk = submissionIds.slice(i, i + CHUNK);
      let offset = 0;
      for (;;) {
        const { data, error } = await this.supabase
          .from('formx_values')
          .select('id, submission_id, field_id, value, created_at')
          .in('submission_id', chunk)
          .order('id')
          .range(offset, offset + PAGE - 1);
        if (error) throw new Error(error.message);
        out.push(...(data ?? []));
        if ((data ?? []).length < PAGE) break;
        offset += PAGE;
      }
    }
    return out;
  }

  async listValuesByField(fieldId: string, limit = 200): Promise<Array<FormxValueDto & { submission: FormxSubmissionDto }>> {
    const { data, error } = await this.supabase
      .from('formx_values')
      .select(`id, submission_id, field_id, value, created_at, submission:formx_submissions!inner(${SUBMISSION_FIELDS}, location:locations!formx_submissions_location_id_fkey(id, name, province:locations_provinces(code, name, full_name)))`)
      .eq('field_id', fieldId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  async listPhotoValuesByProject(projectId: string, limit = 30, offset = 0): Promise<Array<FormxValueDto & { submission: FormxSubmissionDto; fieldLabel: string; formName: string }>> {
    const [{ data: forms, error: fe }, ] = await Promise.all([
      this.supabase.from('formx_forms').select('id, name').eq('project_id', projectId),
    ]);
    if (fe) throw new Error(fe.message);
    const formIds = (forms ?? []).map((f: any) => f.id);
    if (!formIds.length) return [];

    const { data: fields, error: fieldErr } = await this.supabase
      .from('formx_fields')
      .select('id, label, form_id')
      .in('form_id', formIds)
      .eq('field_type', 'photo');
    if (fieldErr) throw new Error(fieldErr.message);
    const fieldIds = (fields ?? []).map((f: any) => f.id);
    if (!fieldIds.length) return [];

    const { data: values, error: ve } = await this.supabase
      .from('formx_values')
      .select(`id, submission_id, field_id, value, created_at, submission:formx_submissions!inner(${SUBMISSION_FIELDS}, location:locations!formx_submissions_location_id_fkey(id, name))`)
      .in('field_id', fieldIds)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);
    if (ve) throw new Error(ve.message);

    const formMap: Record<string, string> = Object.fromEntries((forms ?? []).map((f: any) => [f.id, f.name]));
    const fieldMap: Record<string, { label: string; formId: string }> = Object.fromEntries(
      (fields ?? []).map((f: any) => [f.id, { label: f.label, formId: f.form_id }]),
    );

    return (values ?? []).map((v: any) => ({
      ...v,
      fieldLabel: fieldMap[v.field_id]?.label ?? '',
      formName: formMap[fieldMap[v.field_id]?.formId ?? ''] ?? '',
    }));
  }

  async listKpiFieldsByProject(projectId: string): Promise<Array<FormxFieldDto & { formName: string }>> {
    const { data: forms, error: fe } = await this.supabase.from('formx_forms').select('id, name').eq('project_id', projectId);
    if (fe) throw new Error(fe.message);
    const formIds = (forms ?? []).map((f: any) => f.id);
    if (!formIds.length) return [];

    const { data: fields, error } = await this.supabase
      .from('formx_fields')
      .select(FIELD_FIELDS)
      .in('form_id', formIds)
      .eq('field_type', 'kpi')
      .order('sort_order');
    if (error) throw new Error(error.message);

    const formMap: Record<string, string> = Object.fromEntries((forms ?? []).map((f: any) => [f.id, f.name]));
    return (fields ?? []).map((f: any) => ({ ...f, formName: formMap[f.form_id] ?? '' }));
  }

  async listKpiValuesByProject(projectId: string): Promise<Array<FormxValueDto & { submission: FormxSubmissionDto; fieldLabel: string; formId: string; formName: string; target: number | null; targetUnit: string | null }>> {
    const { data: forms, error: fe } = await this.supabase.from('formx_forms').select('id, name').eq('project_id', projectId);
    if (fe) throw new Error(fe.message);
    const formIds = (forms ?? []).map((f: any) => f.id);
    if (!formIds.length) return [];

    const { data: fields, error: fieldErr } = await this.supabase
      .from('formx_fields')
      .select('id, label, form_id, target, target_unit')
      .in('form_id', formIds)
      .eq('field_type', 'kpi');
    if (fieldErr) throw new Error(fieldErr.message);
    const fieldIds = (fields ?? []).map((f: any) => f.id);
    if (!fieldIds.length) return [];

    const formMap: Record<string, string> = Object.fromEntries((forms ?? []).map((f: any) => [f.id, f.name]));
    const fieldMap: Record<string, { label: string; formId: string; target: number | null; targetUnit: string | null }> = Object.fromEntries(
      (fields ?? []).map((f: any) => [f.id, { label: f.label, formId: f.form_id, target: f.target ?? null, targetUnit: f.target_unit ?? null }]),
    );

    const rows = await fetchAllPaged<any>((from, to) =>
      this.supabase
        .from('formx_values')
        .select(`id, submission_id, field_id, value, created_at, submission:formx_submissions!inner(${SUBMISSION_FIELDS}, location:locations!formx_submissions_location_id_fkey(id, name))`)
        .in('field_id', fieldIds)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to),
    );

    return rows.map((v: any) => {
      const f = fieldMap[v.field_id];
      return {
        ...v,
        fieldLabel: f?.label ?? '',
        formId: f?.formId ?? '',
        formName: formMap[f?.formId ?? ''] ?? '',
        target: f?.target ?? null,
        targetUnit: f?.targetUnit ?? null,
      };
    });
  }

  async getSubmissionWithValues(id: string): Promise<{ submission: FormxSubmissionDto; values: FormxValueDto[] } | null> {
    const [{ data: sub, error: e1 }, { data: vals, error: e2 }] = await Promise.all([
      this.supabase.from('formx_submissions').select(SUBMISSION_FIELDS).eq('id', id).maybeSingle(),
      this.supabase.from('formx_values').select('id, submission_id, field_id, value, created_at').eq('submission_id', id),
    ]);
    if (e1) throw new Error(e1.message);
    if (e2) throw new Error(e2.message);
    if (!sub) return null;
    return { submission: sub, values: vals ?? [] };
  }

  async updateSubmissionWithValues(id: string, input: { locationId?: string | null; notes?: string | null; values: Array<{ fieldId: string; value: string }> }): Promise<void> {
    const { error } = await this.supabase.rpc('update_formx_submission', {
      p_submission_id: id,
      p_location_id: input.locationId ?? null,
      p_notes: input.notes ?? null,
      p_values: input.values.map((value) => ({ field_id: value.fieldId, value: value.value })),
    });
    if (error) throw new Error(error.message);
  }

  async countSubmissionsByProject(projectId: string): Promise<number> {
    const { data: forms, error: fe } = await this.supabase
      .from('formx_forms').select('id').eq('project_id', projectId);
    if (fe) throw new Error(fe.message);
    const formIds = (forms ?? []).map((f: any) => f.id);
    if (!formIds.length) return 0;
    const { count, error } = await this.supabase
      .from('formx_submissions')
      .select('*', { count: 'exact', head: true })
      .in('form_id', formIds);
    if (error) throw new Error(error.message);
    return count ?? 0;
  }
}
